/**
 * SMS Helper Utilities
 * Handles SMS part calculation, character counting, and encoding detection
 */

// GSM 7-bit character set
const GSM_7BIT_CHARS = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

// GSM 7-bit extended characters (count as 2 characters)
const GSM_7BIT_EX = "^{}\\[~]|€";

/**
 * Check if a character is in GSM 7-bit charset
 * @param {string} char - Single character
 * @returns {boolean}
 */
function isGSM7bitChar(char) {
  return GSM_7BIT_CHARS.indexOf(char) !== -1;
}

/**
 * Check if a character is in GSM 7-bit extended charset
 * @param {string} char - Single character
 * @returns {boolean}
 */
function isGSM7bitExtendedChar(char) {
  return GSM_7BIT_EX.indexOf(char) !== -1;
}

/**
 * Detect if a message requires Unicode encoding
 * @param {string} message - The message text
 * @returns {boolean}
 */
export function requiresUnicode(message) {
  if (!message) return false;
  
  for (let i = 0; i < message.length; i++) {
    const char = message.charAt(i);
    if (!isGSM7bitChar(char) && !isGSM7bitExtendedChar(char)) {
      return true;
    }
  }
  return false;
}

/**
 * Calculate character count including extended GSM chars
 * @param {string} message - The message text
 * @returns {number}
 */
export function calculateCharacterCount(message) {
  if (!message) return 0;
  
  let count = 0;
  for (let i = 0; i < message.length; i++) {
    const char = message.charAt(i);
    if (isGSM7bitExtendedChar(char)) {
      count += 2; // Extended chars count as 2
    } else {
      count += 1;
    }
  }
  return count;
}

/**
 * Split message into SMS parts
 * @param {string} message - The message text
 * @returns {Array<string>} Array of message parts
 */
export function splitIntoSMSParts(message) {
  if (!message) return [];
  
  const isUnicode = requiresUnicode(message);
  const singleSMSLimit = isUnicode ? 70 : 160;
  const multiSMSLimit = isUnicode ? 67 : 153; // Slightly less for multi-part due to UDH header
  
  const charCount = isUnicode ? message.length : calculateCharacterCount(message);
  
  // If fits in single SMS
  if (charCount <= singleSMSLimit) {
    return [message];
  }
  
  // Split into multiple parts
  const parts = [];
  let remainingMessage = message;
  let currentCharCount = 0;
  let currentPart = '';
  
  for (let i = 0; i < message.length; i++) {
    const char = message.charAt(i);
    const charLength = isUnicode ? 1 : (isGSM7bitExtendedChar(char) ? 2 : 1);
    
    if (currentCharCount + charLength > multiSMSLimit) {
      // Save current part and start new one
      parts.push(currentPart);
      currentPart = char;
      currentCharCount = charLength;
    } else {
      currentPart += char;
      currentCharCount += charLength;
    }
  }
  
  // Add last part
  if (currentPart) {
    parts.push(currentPart);
  }
  
  return parts;
}

/**
 * Extract plain text from HTML content
 * @param {string} content - HTML or plain text content
 * @returns {string} Plain text
 */
function extractPlainText(content) {
  if (!content) return '';
  
  // Create a temporary div to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = content;
  
  // Replace <br> tags with newlines
  temp.querySelectorAll('br').forEach(br => {
    br.replaceWith('\n');
  });
  
  // Get text content
  let text = temp.textContent || temp.innerText || '';
  
  // Clean up extra whitespace
  text = text.replace(/\u00A0/g, ' '); // Replace non-breaking spaces
  
  return text;
}

/**
 * Calculate SMS statistics
 * @param {string} message - The message text (can include HTML)
 * @returns {Object} { characterCount, messagePartCount, requiresUnicode, parts }
 */
export function calculateSMSStats(message) {
  if (!message) {
    return {
      characterCount: 0,
      messagePartCount: 0,
      requiresUnicode: false,
      parts: []
    };
  }
  
  // Extract plain text from HTML if present
  const plainText = extractPlainText(message);
  
  const isUnicode = requiresUnicode(plainText);
  const charCount = isUnicode ? plainText.length : calculateCharacterCount(plainText);
  const parts = splitIntoSMSParts(plainText);
  
  return {
    characterCount: charCount,
    messagePartCount: parts.length,
    requiresUnicode: isUnicode,
    parts: parts
  };
}

/**
 * Get SMS limit for current message
 * @param {string} message - The message text
 * @param {number} currentPartCount - Current number of parts (0 for first message)
 * @returns {number} Character limit
 */
export function getSMSLimit(message, currentPartCount = 0) {
  const isUnicode = requiresUnicode(message);
  
  if (currentPartCount <= 1) {
    return isUnicode ? 70 : 160;
  } else {
    return isUnicode ? 67 : 153;
  }
}

// ---------------------------------------------------------------------------
// General SMS utilities — migrated from src/mixins/global.js
// ---------------------------------------------------------------------------

/**
 * Strips the leading `+` from a country-code-prefixed phone number string.
 * Returns the input unchanged if it does not start with `+`.
 */
export function countryCodeWithout_plusSign(input) {
  if (input && input.startsWith('+')) return input.slice(1);
  return input;
}

/**
 * Returns true when the text contains `<` or `>` characters that would
 * require SMS transcoding to be enabled on delivery.
 */
export function isTranscodeEnabledForSms(text) {
  return text?.includes('>') || text?.includes('<') ? true : false;
}
